import {useState} from "react";
import {useTranslation} from "react-i18next";
import {css, Theme} from "@emotion/react";
import {elevation} from "@pg-design/elevation";
import {backgroundColor, borderRadius, flex, h100, onDesktop, order, p} from "@pg-design/helpers-css";
import {Select} from "@pg-design/select-v2";
import {Text} from "@pg-design/text";

import {flexGap} from "../../../components/css_helpers/flex";
import {SUBSCRIPTION_PERIODS} from "./BdSubscriptionBox";
import {Price} from "./Price";

type SUBSCRIPTION_CITIES =
    | "warsaw-and-agglomeration"
    | "wroclaw"
    | "poznan"
    | "gdansk"
    | "lodz"
    | "krakow"
    | "katowice"
    | "szczecin"
    | "olsztyn"
    | "bialystok"
    | "bydgoszcz"
    | "kielce"
    | "lublin"
    | "rzeszow";

const SUBSCRIPTION_PRICES = {
    "warsaw-and-agglomeration": {
        monthly: {
            standard: "950",
            rpClient: "570"
        },
        annually: {
            standard: "799",
            rpClient: "480"
        }
    },
    wroclaw: {
        monthly: {
            standard: "720",
            rpClient: "432"
        },
        annually: {
            standard: "599",
            rpClient: "359"
        }
    },
    poznan: {
        monthly: {
            standard: "720",
            rpClient: "432"
        },
        annually: {
            standard: "599",
            rpClient: "359"
        }
    },
    gdansk: {
        monthly: {
            standard: "720",
            rpClient: "432"
        },
        annually: {
            standard: "599",
            rpClient: "359"
        }
    },
    lodz: {
        monthly: {
            standard: "720",
            rpClient: "432"
        },
        annually: {
            standard: "599",
            rpClient: "359"
        }
    },
    krakow: {
        monthly: {
            standard: "720",
            rpClient: "432"
        },
        annually: {
            standard: "599",
            rpClient: "359"
        }
    },
    katowice: {
        monthly: {
            standard: "720",
            rpClient: "432"
        },
        annually: {
            standard: "599",
            rpClient: "359"
        }
    },
    szczecin: {
        monthly: {
            standard: "360",
            rpClient: "216"
        },
        annually: {
            standard: "299",
            rpClient: "179"
        }
    },
    olsztyn: {
        monthly: {
            standard: "360",
            rpClient: "216"
        },
        annually: {
            standard: "299",
            rpClient: "179"
        }
    },
    bialystok: {
        monthly: {
            standard: "360",
            rpClient: "216"
        },
        annually: {
            standard: "299",
            rpClient: "179"
        }
    },
    bydgoszcz: {
        monthly: {
            standard: "360",
            rpClient: "216"
        },
        annually: {
            standard: "299",
            rpClient: "179"
        }
    },
    kielce: {
        monthly: {
            standard: "360",
            rpClient: "216"
        },
        annually: {
            standard: "299",
            rpClient: "179"
        }
    },
    lublin: {
        monthly: {
            standard: "360",
            rpClient: "216"
        },
        annually: {
            standard: "299",
            rpClient: "179"
        }
    },
    rzeszow: {
        monthly: {
            standard: "360",
            rpClient: "216"
        },
        annually: {
            standard: "299",
            rpClient: "179"
        }
    }
};

interface ISelectedCityPricingBoxProps {
    isRpClient: boolean;
    paymentPeriod: SUBSCRIPTION_PERIODS;
}

export const SelectedCityPricingBox = ({isRpClient, paymentPeriod}: ISelectedCityPricingBoxProps) => {
    const {t} = useTranslation();
    const [selectedCity, setSelectedCity] = useState<SUBSCRIPTION_CITIES>("warsaw-and-agglomeration");

    const options: {value: SUBSCRIPTION_CITIES; label: string}[] = [
        {
            value: "warsaw-and-agglomeration",
            label: `${t("city.warszawa.nominative")} + ${t("pricing.agglomeration")}`
        },
        {
            value: "wroclaw",
            label: `${t("city.wroclaw.nominative")} + ${t("pricing.agglomeration")}`
        },
        {
            value: "poznan",
            label: `${t("city.poznan.nominative")} + ${t("pricing.agglomeration")}`
        },
        {
            value: "gdansk",
            label: `${t("city.gdansk.nominative")} + ${t("pricing.agglomeration")}`
        },
        {
            value: "lodz",
            label: `${t("city.lodz.nominative")} + ${t("pricing.agglomeration")}`
        },
        {
            value: "krakow",
            label: `${t("city.krakow.nominative")} + ${t("pricing.agglomeration")}`
        },
        {
            value: "katowice",
            label: `${t("city.katowice.nominative")} + ${t("pricing.agglomeration")}`
        },
        {
            value: "szczecin",
            label: t("city.szczecin.nominative")
        },
        {
            value: "olsztyn",
            label: t("city.olsztyn.nominative")
        },
        {
            value: "bialystok",
            label: t("city.bialystok.nominative")
        },
        {
            value: "bydgoszcz",
            label: t("city.bydgoszcz.nominative")
        },
        {
            value: "kielce",
            label: t("city.kielce.nominative")
        },
        {
            value: "lublin",
            label: t("city.lublin.nominative")
        },
        {
            value: "rzeszow",
            label: t("city.rzeszow.nominative")
        }
    ];

    const price = SUBSCRIPTION_PRICES[selectedCity][paymentPeriod][isRpClient ? "rpClient" : "standard"];

    return (
        <div css={content}>
            <div css={header}>
                <Text variant="headline_4" as="p">
                    {t("pricing.bd_subscription.selected_city")}
                </Text>
                <Select
                    css={select}
                    instanceId="pricing_header_city_select"
                    onChange={(val) => {
                        if (val) {
                            setSelectedCity(val as SUBSCRIPTION_CITIES);
                        }
                    }}
                    options={options}
                    value={selectedCity}
                />
            </div>

            <Text css={descriptionWrapper} variant="body_copy_1">
                {t("pricing.bd_subscription.selected_city_description_1")}
                <b>{t("pricing.bd_subscription.selected_city_description_2")}</b>
                {t("pricing.bd_subscription.selected_city_description_3")}
            </Text>

            <Price price={price} primaryButton={{text: t("pricing.order"), href: "#contact"}} />
        </div>
    );
};

const divider = (theme: Theme) => css`
    border-bottom: 1px solid ${theme.colors.gray[300]};
`;

const header = (theme: Theme) => css`
    ${p(4, 2, 4, 2)};
    ${backgroundColor(theme.colors.gray["100"])};
    ${borderRadius(2, 2, 0, 0)};
    ${flex("center", "space-between")};
    min-height: 62px;
    ${onDesktop(css`
        min-height: 112px;
    `)}
    flex-wrap: wrap;
    ${flexGap(1)};
`;

const descriptionWrapper = (theme: Theme) => css`
    ${p(3, 2, 3, 2)};
    ${divider(theme)};
`;

const content = css`
    ${borderRadius(2)};
    ${elevation(2)};
    background: #fff;
    ${h100};
`;

const select = css`
    ${order(2)};
    max-width: 100%;
    min-width: 50%;

    ${onDesktop(css`
        min-width: 35%;
        max-width: 50%;
        ${order(3)}
    `)};
`;
